import { useMemo, MouseEvent } from 'react';
import { isNotNull } from '@yi/core';
import { getHelpCenterUrl } from '@yi/core/src/help-center-urls';
import { i18n_about } from '@yi/i18n/phrases/i18n_about';
import { i18n_articles } from '@yi/i18n/phrases/i18n_articles';
import { i18n_ashtanga } from '@yi/i18n/phrases/i18n_ashtanga';
import { i18n_ayurveda } from '@yi/i18n/phrases/i18n_ayurveda';
import { i18n_breathwork } from '@yi/i18n/phrases/i18n_breathwork';
import { i18n_careers } from '@yi/i18n/phrases/i18n_careers';
import { i18n_challenges } from '@yi/i18n/phrases/i18n_challenges';
import { i18n_cookie_preferences } from '@yi/i18n/phrases/i18n_cookie_preferences';
import { i18n_courses } from '@yi/i18n/phrases/i18n_courses';
import { i18n_docs_and_films } from '@yi/i18n/phrases/i18n_docs_and_films';
import { i18n_for_teachers } from '@yi/i18n/phrases/i18n_for_teachers';
import { i18n_hatha_yoga } from '@yi/i18n/phrases/i18n_hatha_yoga';
import { i18n_kundalini } from '@yi/i18n/phrases/i18n_kundalini';
import { i18n_meditations } from '@yi/i18n/phrases/i18n_meditations';
import { i18n_more } from '@yi/i18n/phrases/i18n_more';
import { i18n_need_help } from '@yi/i18n/phrases/i18n_need_help';
import { i18n_our_teachers } from '@yi/i18n/phrases/i18n_our_teachers';
import { i18n_podcasts } from '@yi/i18n/phrases/i18n_podcasts';
import { i18n_poses } from '@yi/i18n/phrases/i18n_poses';
import { i18n_practice } from '@yi/i18n/phrases/i18n_practice';
import { i18n_premium_trainings } from '@yi/i18n/phrases/i18n_premium_trainings';
import { i18n_prenatal_and_postpartum } from '@yi/i18n/phrases/i18n_prenatal_and_postpartum';
import { i18n_privacy_policy } from '@yi/i18n/phrases/i18n_privacy_policy';
import { i18n_restorative } from '@yi/i18n/phrases/i18n_restorative';
import { i18n_teacher_resources } from '@yi/i18n/phrases/i18n_teacher_resources';
import { i18n_teachers } from '@yi/i18n/phrases/i18n_teachers';
import { i18n_terms_of_service } from '@yi/i18n/phrases/i18n_terms_of_service';
import { i18n_tutorials } from '@yi/i18n/phrases/i18n_tutorials';
import { i18n_vinyasa } from '@yi/i18n/phrases/i18n_vinyasa';
import { i18n_yin } from '@yi/i18n/phrases/i18n_yin';
import { i18n_yoga_classes } from '@yi/i18n/phrases/i18n_yoga_classes';
import { i18n_yoga_therapy } from '@yi/i18n/phrases/i18n_yoga_therapy';

import { Locale } from '~/common';
import { oneTrust } from '~/src/common/oneTrust/oneTrust';
import {
  CHALLENGES_PATHNAME,
  COURSES_PATHNAME,
  PODCASTS_PATHNAME,
  PREMIUM_TRAININGS_PATHNAME,
  SERIES_PATHNAME,
  TEACHERS_PATHNAME,
} from '~/src/common/routes';
import { useCurrentUser } from '~/src/common/user';

import { useLocale } from '../../../LocaleProvider';

type Link = {
  name: string;
  url: string;
  external?: boolean;
  onClick?: (e: MouseEvent) => void;
};
type LinkGroup = {
  title: string;
  items: Array<Link>;
};

type LinkGroupsList = Array<LinkGroup>;

function getESLinkGroups(isLoggedIn: boolean): LinkGroupsList {
  const locale: Locale = 'es';

  return [
    {
      title: i18n_yoga_classes(locale),
      items: [
        { name: i18n_vinyasa(locale), url: `/classes?style=Vinyasa` },
        { name: i18n_hatha_yoga(locale), url: `/classes?style=Hatha+Yoga` },
        { name: i18n_yin(locale), url: `/classes?style=Yin` },
        { name: i18n_restorative(locale), url: `/classes?style=Restaurativo` },
        { name: i18n_kundalini(locale), url: `/classes?style=Kundalini` },
        {
          name: i18n_prenatal_and_postpartum(locale),
          url: `/classes?style=Prenatal+y+posparto`,
        },
        { name: i18n_ashtanga(locale), url: `/classes?style=Ashtanga` },
      ],
    },
    {
      title: i18n_meditations(locale),
      items: [
        { name: 'Meditación guiada', url: `/meditations?style=Meditación+guiada` },
        { name: i18n_breathwork(locale), url: `/meditations?style=Breathwork` },
        { name: 'Relajación guiada', url: `/meditations?style=Relajación+guiada` },
      ],
    },
    {
      title: i18n_articles(locale),
      items: [
        { name: i18n_practice(locale), url: `/articles?topic=Practicar` },
        { name: i18n_yoga_therapy(locale), url: `/articles/?topic=Terapia+de+yoga` },
        { name: i18n_ayurveda(locale), url: `/articles?topic=Ayurveda` },
        { name: i18n_teacher_resources(locale), url: `/articles/?topic=Para+maestros` },
      ],
    },
    {
      title: i18n_teachers(locale),
      items: [
        { name: 'Gaby Zermeño', url: '/profile/gaby-zermeno' },
        { name: 'Emma Marín', url: '/profile/emma-marin' },
        { name: 'Laila Kuri', url: '/profile/laila-kuri' },
        { name: 'Lygia Lima', url: '/profile/lygia-lima' },
        { name: 'Alex Quiyono', url: '/profile/alex-quiyono' },
        { name: 'Esteban Salazar', url: '/profile/esteban-salazar' },
        { name: 'Twiggy Romero', url: '/profile/twiggy-romero' },
        { name: 'Durga Stef', url: '/profile/durga-stef' },
      ],
    },
    {
      title: i18n_more(locale),
      items: [
        { name: i18n_courses(locale), url: COURSES_PATHNAME },
        { name: i18n_premium_trainings(locale), url: PREMIUM_TRAININGS_PATHNAME },
        { name: i18n_docs_and_films(locale), url: SERIES_PATHNAME },
      ],
    },

    {
      title: i18n_about(locale),
      items: [
        {
          name: i18n_need_help(locale),
          url: getHelpCenterUrl('home', 'es'),
          external: true,
        },
        {
          name: i18n_privacy_policy(locale),
          url: '/terms-of-service#toc-privacy-policy',
          external: false,
        },
        {
          name: i18n_terms_of_service(locale),
          url: '/terms-of-service',
          external: false,
        },
        isLoggedIn
          ? null
          : {
              name: i18n_cookie_preferences(locale),
              url: '#',
              external: true,
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                oneTrust.showManageCookiesModal();
              },
            },
      ].filter(isNotNull),
    },
  ];
}

function getENLinkGroups(isLoggedIn: boolean): LinkGroupsList {
  const locale: Locale = 'en';

  return [
    {
      title: i18n_yoga_classes(locale),
      items: [
        { name: i18n_vinyasa(locale), url: `/classes?style=Vinyasa` },
        { name: i18n_hatha_yoga(locale), url: `/classes?style=Hatha+Yoga` },
        { name: i18n_yin(locale), url: `/classes?style=Yin` },
        { name: i18n_restorative(locale), url: `/classes?style=Restorative` },
        { name: i18n_kundalini(locale), url: `/classes?style=Kundalini` },
        {
          name: i18n_prenatal_and_postpartum(locale),
          url: `/classes?style=Prenatal+and+Postpartum`,
        },
        { name: i18n_ashtanga(locale), url: `/classes?style=Ashtanga` },
      ],
    },
    {
      title: i18n_meditations(locale),
      items: [
        { name: 'Sleep Better', url: `/meditations?focus=Sleep+Better` },
        { name: 'Reduce Stress', url: `/meditations?focus=Reduce+Stress` },
        { name: 'Morning Meditations', url: `/meditations?focus=Morning` },
        { name: 'Get Focused', url: `/meditations?focus=Get+Focused` },
      ],
    },

    {
      title: i18n_breathwork(locale),
      items: [
        { name: 'Calming', url: `/pranayama?algolia_focus=Calming` },
        { name: 'Energizing', url: `/pranayama?algolia_focus=Energizing` },
        { name: 'Reduce Stress', url: `/pranayama?algolia_focus=Reduce+Stress` },
      ],
    },
    {
      title: i18n_articles(locale),
      items: [
        { name: i18n_practice(locale), url: `/articles?topic=Practice` },
        { name: i18n_yoga_therapy(locale), url: `/articles?topic=Yoga+Therapy` },
        { name: i18n_ayurveda(locale), url: `/articles?topic=Ayurveda` },
        { name: i18n_poses(locale), url: `/poses` },
        { name: i18n_for_teachers(locale), url: `/articles/?topic=For+Teachers` },
        { name: i18n_tutorials(locale), url: `/articles/?topic=Tutorials` },
      ],
    },
    {
      title: i18n_more(locale),
      items: [
        { name: i18n_courses(locale), url: COURSES_PATHNAME },
        { name: i18n_challenges(locale), url: CHALLENGES_PATHNAME },
        { name: i18n_docs_and_films(locale), url: SERIES_PATHNAME },
        { name: i18n_our_teachers(locale), url: TEACHERS_PATHNAME },
        { name: i18n_premium_trainings(locale), url: PREMIUM_TRAININGS_PATHNAME },
        { name: i18n_podcasts(locale), url: PODCASTS_PATHNAME },
      ],
    },
    {
      title: i18n_about(locale),
      items: [
        {
          name: i18n_need_help(locale),
          url: getHelpCenterUrl('home', 'en'),
          external: true,
        },
        { name: i18n_careers(locale), url: `/careers` },
        {
          name: i18n_privacy_policy(locale),
          url: '/terms-of-service#toc-privacy-policy',
          external: false,
        },
        {
          name: i18n_terms_of_service(locale),
          url: `/terms-of-service/`,
          external: false,
        },
        isLoggedIn
          ? null
          : {
              name: i18n_cookie_preferences(locale),
              url: '#',
              external: true,
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                oneTrust.showManageCookiesModal();
              },
            },
      ].filter(isNotNull),
    },
  ];
}

export function useLinkGroups(): LinkGroupsList {
  const { locale } = useLocale();
  const { isLoggedIn } = useCurrentUser();
  const LinkGroups = useMemo(() => {
    const groupedLinks =
      locale === 'es' ? getESLinkGroups(isLoggedIn) : getENLinkGroups(isLoggedIn);
    return groupedLinks;
  }, [locale, isLoggedIn]);
  return LinkGroups;
}
